// @LTG-ID: data/journals/exit-planning-guide
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 2 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "exit-planning-guide",
    FRONTMATTER: {
        number: "39",
        section: "Exit Planning",
        sectionHref: "/journals#exit-planning",
        sectionAnchor: "exit-planning",
        title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
        deck: "Exit planning is the process of preparing a business, and its owner, for an eventual ownership transition.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-04-16",
        publishedHuman: "April 16, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 4,
        wordCount: 664,
        timeRequired: "PT4M",
        keywords: "exit planning for business owners, how to exit a business, business exit strategy, succession planning small business",
        articleSection: "Exit Planning",
        articleBodyPreview: "Exit planning is the process of preparing a business, and its owner, for an eventual ownership transition. It typically covers business value, transferability, tax structure, personal financial readiness, and life after the sale. Most advisors recommend starting three to five years before a planned exit, because the highest impact improvements take time. Every owner exits. That is not a marketing line, it is arithmetic. The only variables are when, how, to whom, and on whose terms. Exit planning",
        complianceNote: "This article is educational and does not constitute financial, legal, or tax advice. Consult qualified professionals about your specific situation.",
        needsDisclaimerFooter: true,
        internalLinks: [
        "how-much-is-my-business-worth",
        "how-long-does-it-take-to-sell-a-business",
        "what-percentage-of-businesses-sell",
        "owner-dependence-business-value",
        "silver-tsunami-business-statistics"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Exit Planning 101: Leave Your Business on Your Terms · LTG",
        canonical: "https://letstalkgrowth.com/journals/exit-planning-guide",
        ogImage: "/journals/og/exit-planning-guide.jpg",
    },
    BODY_MARKDOWN: `Exit planning is the process of preparing a business, and its owner, for an eventual ownership transition. It typically covers business value, transferability, tax structure, personal financial readiness, and life after the sale. Most advisors recommend starting three to five years before a planned exit, because the highest-impact improvements take time.

Every owner exits. That is not a marketing line, it is arithmetic. The only variables are when, how, to whom, and on whose terms. Exit planning is simply the discipline of making those variables choices instead of accidents.

## Why this deserves your attention now, not later

The Exit Planning Institute's State of Owner Readiness research has consistently found that the large majority of an average owner's net worth is locked inside the business, yet most owners have no written transition plan. The same body of research reports that a striking share of owners who do sell come to regret the transaction within a year, most often because they were unprepared personally, not financially.

Layer on the demographics: millions of American businesses are owned by baby boomers approaching or past traditional retirement age, a wave often called the silver tsunami. When more sellers than buyers arrive in a market, prepared sellers win and unprepared sellers compromise. Preparation is the entire game.

## The five questions every exit plan must answer

**1. What is the business worth today, and what could it be worth?**
You cannot plan a journey without knowing the starting point. An honest, market-based estimate of current value, and a clear-eyed view of the gap between current value and potential value, anchors everything else. Our valuation guide walks through how that number comes together.

**2. Is the business transferable?**
Transferability is the degree to which the business's value survives your departure. Owner dependence, undocumented processes, handshake customer relationships, and thin management all reduce transferability. These are fixable, but they are slow to fix, which is why runway matters.

**3. Which exit path fits?**
There are more doors than most owners realize: a sale to a third party, a sale to employees or management, a family transition, an employee stock ownership plan, a partial sale to a financial partner, or an orderly wind-down. Each path has different economics, timelines, and emotional profiles. The right one depends on your goals, your people, and your market.

**4. What do you personally need, and what do you want?**
There is a number at which work becomes optional. Knowing that number, with real math behind it rather than a guess, changes negotiations completely. An owner who knows their walk-away number negotiates from strength. An owner who does not is negotiating blind.

**5. What happens the day after?**
Identity is the quiet killer of exit satisfaction. Owners who have somewhere to go, not just something to leave, report far better post-sale outcomes. This is not soft advice; it is the difference between the sellers who celebrate their anniversary and the ones who spend it regretting the deal.

## A realistic timeline

**Three to five years out:** establish baseline value, fix financial record quality, begin reducing owner dependence, and start shifting revenue toward recurring and contracted forms. These moves have the largest effect on value and take the longest.

**One to three years out:** deepen the management team, resolve customer concentration where possible, clean up legal housekeeping such as contracts, leases, and entity structure, and begin tax planning with your CPA, because the most valuable tax strategies must be in place well before a letter of intent.

**The final year:** assemble the deal team, prepare diligence materials, and keep performance up. Buyers pay for trailing results and forward momentum. A business that sags during the sale process invites price renegotiation.

## The most common mistake

Waiting for a trigger. Health events, partner disputes, burnout, and unsolicited offers force exits at the worst possible moments, and forced sellers accept discounts. The owners who capture full value are almost always the ones who prepared before they needed to.`,
    FAQ: [
        { question: "When should I start exit planning?", answer: "Three to five years before a target exit is the common professional recommendation, because the highest-value improvements, such as reducing owner dependence and building recurring revenue, take years to show up in the numbers a buyer will pay for." },
        { question: "Is exit planning only about selling?", answer: "No. A strong exit plan improves the business whether or not you ever sell. Reduced owner dependence, cleaner financials, and deeper teams make a business more profitable and easier to run today, which is why exit planning is often described as simply good strategy." },
        { question: "What professionals are involved in exit planning?", answer: "Commonly a CPA for tax and financial structure, an attorney for legal readiness, a financial planner for personal wealth, and depending on path, a business broker or M&A advisor for the transaction itself." },
        { question: "What is the difference between exit planning and succession planning?", answer: "Succession planning focuses on who will lead and own the business next, often within a family or management team. Exit planning is broader, covering value, transferability, taxes, the owner's personal finances, and life after ownership." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Exit Planning",
            title: "What Percentage of Businesses Actually Sell? The Statistics Every Owner Should See",
            meta: "3 min · Brief 44",
            to: "/journals/what-percentage-of-businesses-sell",
        },
    ],
};

export default BRIEF;
